import type { RulesConfig } from '@halvard/rules';

/** Experimental rule options a local or bot game can switch on. */
export type ExperimentKey = 'tall' | 'twin' | 'home' | 'fog' | 'noLookout' | 'escort';

/**
 * The board (a generated mountain or the Classic one) and the experimental options switched on.
 * Options marked `seedOnly` below only apply to generated mountains.
 */
export type Mountain = ({ kind: 'seed'; seed: number } | { kind: 'classic' }) &
  Partial<Record<ExperimentKey, boolean>>;

export interface Experiment {
  key: ExperimentKey;
  /** URL parameter and the value that switches it on. */
  param: string;
  value: string;
  label: string;
  description: string;
  /** Only for generated mountains (it changes how the mountain is built). */
  seedOnly: boolean;
  /** Options that cannot be on at the same time as this one; turning it on turns them off. */
  excludes?: readonly ExperimentKey[];
  config: Partial<RulesConfig>;
}

/** Every experimental option, in the order they are offered. */
export const EXPERIMENTS: readonly Experiment[] = [
  {
    key: 'noLookout',
    param: 'nolookout',
    value: '1',
    label: 'No Lookout',
    description: 'Each camp starts without its Lookout.',
    seedOnly: false,
    config: { lookout: false },
  },
  {
    key: 'escort',
    param: 'escort',
    value: '1',
    label: 'Escorted Beacon',
    description: 'The Beacon only steps with a friendly piece next to it, and cannot be captured.',
    seedOnly: false,
    config: { beaconMode: 'escorted' },
  },
  {
    key: 'twin',
    param: 'peaks',
    value: '2',
    label: 'Two summits',
    description: "One summit per camp; reach the other camp's summit to win.",
    seedOnly: true,
    excludes: ['home'],
    config: { summitMode: 'twin' },
  },
  {
    key: 'home',
    param: 'peaks',
    value: 'home',
    label: 'Mountain fortresses',
    description:
      "Each Beacon starts on its own peak; no summit win, capture the other camp's Beacon.",
    seedOnly: true,
    excludes: ['twin'],
    config: { summitMode: 'home' },
  },
  {
    key: 'tall',
    param: 'tall',
    value: '1',
    label: 'Tall mountain',
    description: 'Heights 0–6, summit at 6.',
    seedOnly: true,
    config: { maxHeight: 6 },
  },
  {
    key: 'fog',
    param: 'fog',
    value: '1',
    label: 'Fog of war',
    description: 'Each player sees only what their pieces see.',
    seedOnly: false,
    config: { fog: true },
  },
];

/** A random 32-bit seed for a new mountain. */
export function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] ?? 0;
}

/** Parses a seed from a URL; only integers 0 to 2³² − 1 are valid. */
export function parseSeed(text: string | null | undefined): number | null {
  if (!text || !/^\d{1,10}$/.test(text)) return null;
  const seed = Number(text);
  return seed <= 0xffffffff ? seed : null;
}

/** Whether an option applies to this mountain. */
export function isOn(mountain: Mountain, key: ExperimentKey): boolean {
  const experiment = EXPERIMENTS.find((e) => e.key === key);
  return mountain[key] === true && !(experiment?.seedOnly && mountain.kind === 'classic');
}

export function isTall(mountain: Mountain): boolean {
  return isOn(mountain, 'tall');
}

export function isTwin(mountain: Mountain): boolean {
  return isOn(mountain, 'twin');
}

/** The options switched on, to carry over to another mountain. */
export function flagsOf(mountain: Mountain): Partial<Record<ExperimentKey, boolean>> {
  const flags: Partial<Record<ExperimentKey, boolean>> = {};
  for (const e of EXPERIMENTS) if (mountain[e.key]) flags[e.key] = true;
  return flags;
}

/** The rules of a game on this mountain. */
export function configFor(mountain: Mountain): Partial<RulesConfig> {
  return Object.assign(
    {},
    ...EXPERIMENTS.filter((e) => isOn(mountain, e.key)).map((e) => e.config),
  ) as Partial<RulesConfig>;
}

/** The options with one switched on or off, turning off the options it excludes. */
export function withExperiment(
  flags: Partial<Record<ExperimentKey, boolean>>,
  key: ExperimentKey,
  on: boolean,
): Partial<Record<ExperimentKey, boolean>> {
  const next = { ...flags, [key]: on };
  if (on)
    for (const other of EXPERIMENTS.find((e) => e.key === key)?.excludes ?? []) next[other] = false;
  return next;
}

/** The mountain with one option flipped. Turning on a mountain-only option leaves the Classic board. */
export function toggleExperiment(mountain: Mountain, key: ExperimentKey): Mountain {
  const on = !isOn(mountain, key);
  const experiment = EXPERIMENTS.find((e) => e.key === key);
  const flags = withExperiment(flagsOf(mountain), key, on);
  if (on && experiment?.seedOnly && mountain.kind === 'classic') {
    return { ...flags, kind: 'seed', seed: randomSeed() };
  }
  return mountain.kind === 'classic'
    ? { ...flags, kind: 'classic' }
    : { ...flags, kind: 'seed', seed: mountain.seed };
}

function kindOf(mountain: Mountain): Mountain {
  return mountain.kind === 'classic' ? { kind: 'classic' } : { kind: 'seed', seed: mountain.seed };
}

/** `?seed=123` or `?board=classic`, plus one parameter per experimental option. */
export function mountainFromParams(params: URLSearchParams): Mountain | null {
  let mountain: Mountain;
  if (params.get('board') === 'classic') mountain = { kind: 'classic' };
  else {
    const seed = parseSeed(params.get('seed'));
    if (seed === null) return null;
    mountain = { kind: 'seed', seed };
  }
  for (const e of EXPERIMENTS) {
    if (params.get(e.param) === e.value && !(e.seedOnly && mountain.kind === 'classic')) {
      mountain = { ...withExperiment(flagsOf(mountain), e.key, true), ...kindOf(mountain) };
    }
  }
  return mountain;
}

export function mountainParams(mountain: Mountain): Record<string, string> {
  const params: Record<string, string> =
    mountain.kind === 'classic' ? { board: 'classic' } : { seed: String(mountain.seed) };
  for (const e of EXPERIMENTS) if (isOn(mountain, e.key)) params[e.param] = e.value;
  return params;
}

/** A key that changes whenever the board or the experimental options change. */
export function mountainKey(mountain: Mountain): string {
  return new URLSearchParams(mountainParams(mountain)).toString();
}

/** "Mountain #42 · two summits · fog of war" */
export function mountainName(mountain: Mountain): string {
  const base =
    mountain.kind === 'classic'
      ? 'Classic mountain'
      : `${isTall(mountain) ? 'Tall mountain' : 'Mountain'} #${mountain.seed}`;
  const extras = EXPERIMENTS.filter((e) => e.key !== 'tall' && isOn(mountain, e.key)).map((e) =>
    e.label.toLowerCase(),
  );
  return [base, ...extras].join(' · ');
}
